import { getOverallRelativeTiming } from './championship-class-scoring'

// Presentation only. Never write these positions or timing values back to results.
const STATUS_ORDER = { finished: 0, dnf_points: 1, dnf: 2, dns: 3, dsq: 4 }

function number(value) {
  if (value == null || value === '' || typeof value === 'boolean') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function nonNegative(value) {
  const parsed = number(value)
  return parsed != null && parsed >= 0 ? parsed : null
}

/** Safe, minimal timing facts for the internal viewer API and editor preview. */
export function getResultDisplayTiming(result, sessionType) {
  const metadata = result.metadata?.displayTimingInvalidated
    ? {}
    : result.metadata || {}
  const penalty = number(result.penaltyTime ?? 0)
  const value = nonNegative(result.value)
  const laps = nonNegative(result.laps)
  let absolute = null
  let effective = null

  if (sessionType === 'race') {
    absolute =
      nonNegative(metadata.finishTime) ?? nonNegative(metadata.totalTime)
  } else {
    absolute =
      nonNegative(metadata.bestLap) ?? nonNegative(metadata.originalBestLap)
    if (absolute === 0) absolute = null
  }

  // TIME is an editable absolute value; imported metadata can predate an edit.
  if (result.type === 'TIME') absolute = value
  if (absolute != null && absolute > 0 && penalty != null)
    effective = absolute + penalty

  // This reference is recorded by the class-relative timing conversion. Its
  // GAP already contains the penalty, unlike an imported absolute finish time.
  if (effective == null && result.type === 'GAP' && value != null) {
    const reference = nonNegative(metadata.classLeaderEffectiveFinishTime)
    if (
      sessionType === 'race' &&
      metadata.classRelativeTimingVersion === 1 &&
      reference != null &&
      reference > 0
    ) {
      effective = reference + value
    }
  }

  if (effective != null && effective <= 0) {
    effective = null
  }
  return {
    effectiveTime: effective,
    laps: laps != null && Number.isInteger(laps) ? laps : null,
  }
}

export function adaptEditorResults(
  results,
  sessionType,
  getCarClass = () => null,
) {
  return results
    .filter((result) => !result.deleted)
    .map((result, sourceOrder) => ({
      sourceOrder,
      id: result.id,
      result,
      status: result.status,
      splitId: result.metadata?.splitId ?? null,
      carClassId: getCarClass(result)?.id ?? null,
      timing: getResultDisplayTiming(result, sessionType),
    }))
}

export function adaptViewerResults(results = []) {
  return results.map((result) => ({
    sourceOrder: number(result.racePosition) ?? Number.MAX_SAFE_INTEGER,
    id: result.resultId ?? result.user?.id,
    result,
    status: result.status,
    splitId: result.split?.id ?? null,
    carClassId: result.carClass?.id ?? null,
    timing: result.displayTiming ?? { effectiveTime: null, laps: null },
  }))
}

export function getDisplayTiming(row, leader, sessionType) {
  if (row.status !== 'finished' || !leader) return null
  if (row.id === leader.id) return { displayValue: 'Leader' }
  const time = row.timing.effectiveTime
  const leaderTime = leader.timing.effectiveTime
  if (
    sessionType === 'race' &&
    row.timing.laps != null &&
    leader.timing.laps != null
  ) {
    const difference = leader.timing.laps - row.timing.laps
    if (difference > 0) return { time: difference, type: 'LAP', penaltyTime: 0 }
  }
  if (time == null || leaderTime == null) {
    // Historical GAP/LAP values still have meaning within their original
    // classification. Reuse that display logic, never for cross-split sorting.
    const timingResult = ({ result }) => ({
      ...result,
      time: number(result.value ?? result.time),
      classRelativeTimingVersion:
        result.classRelativeTimingVersion ??
        result.metadata?.classRelativeTimingVersion,
    })
    const result = timingResult(row)
    const reference = timingResult(leader)
    if (
      result.time == null ||
      reference.time == null ||
      (result.type === 'TIME' && result.time <= 0) ||
      (reference.type === 'TIME' &&
        reference.time <= 0 &&
        !(reference.time === 0 && result.type === 'GAP'))
    )
      return { displayValue: 'Unavailable' }
    return getOverallRelativeTiming(result, reference, { sessionType })
  }
  if (time < leaderTime) return { displayValue: 'Unavailable' }
  return { time: time - leaderTime, type: 'GAP', penaltyTime: 0 }
}

export function buildOverallDisplay(rows, sessionType, splits = []) {
  const splitOrder = new Map(splits.map((split, index) => [split.id, index]))
  const comparable = (row) =>
    Number.isFinite(row.timing.effectiveTime) &&
    row.timing.effectiveTime > 0 &&
    (sessionType !== 'race' ||
      (Number.isInteger(row.timing.laps) && row.timing.laps >= 0))
  const rank = (row) =>
    row.status === 'finished'
      ? comparable(row)
        ? 0
        : 6
      : (STATUS_ORDER[row.status] ?? 5)
  const incompleteCount = rows.filter((row) => rank(row) === 6).length
  const ordered = rows.slice().sort((a, b) => {
    const statusDifference = rank(a) - rank(b)
    if (statusDifference) return statusDifference
    if (rank(a) === 0) {
      const lapDifference =
        sessionType === 'race' ? b.timing.laps - a.timing.laps : 0
      const timeDifference = a.timing.effectiveTime - b.timing.effectiveTime
      if (lapDifference || timeDifference)
        return lapDifference || timeDifference
    }
    if (rank(a) === 6) {
      const splitDifference =
        (splitOrder.get(a.splitId) ?? splits.length) -
        (splitOrder.get(b.splitId) ?? splits.length)
      const sourceDifference = (a.sourceOrder ?? 0) - (b.sourceOrder ?? 0)
      if (splitDifference || sourceDifference)
        return splitDifference || sourceDifference
    }
    return String(a.id).localeCompare(String(b.id))
  })
  const leader = ordered.find((row) => rank(row) === 0)
  return {
    available: true,
    reason: null,
    incompleteCount,
    notice: incompleteCount
      ? 'Some finished drivers have missing or incomparable timing and are shown below all other results.'
      : null,
    rows: ordered.map((row, index) => ({
      ...row,
      position: index + 1,
      timingOverride:
        rank(row) === 6
          ? { displayValue: 'Unavailable' }
          : getDisplayTiming(row, leader, sessionType),
    })),
  }
}

export function buildSplitDisplay(rows, sessionType) {
  const groups = new Map()
  for (const row of rows) {
    if (!groups.has(row.splitId)) groups.set(row.splitId, [])
    groups.get(row.splitId).push(row)
  }
  return Array.from(groups, ([splitId, group]) => {
    const leader = group.find((row) => row.status === 'finished')
    return {
      splitId,
      rows: group.map((row, index) => ({
        ...row,
        position: index + 1,
        timingOverride: getDisplayTiming(row, leader, sessionType),
      })),
    }
  })
}

export function formatDisplayGap(timing) {
  if (!timing) return null
  if (timing.displayValue) return timing.displayValue
  if (timing.type === 'LAP')
    return `+${timing.time} lap${timing.time === 1 ? '' : 's'}`
  const milliseconds = Math.round(timing.time)
  const minutes = Math.floor(milliseconds / 60000)
  const seconds = Math.floor((milliseconds % 60000) / 1000)
  return `+${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(milliseconds % 1000).padStart(3, '0')}`
}

/** Build combined classifications before filtering; at least one combine option must be enabled. */
export function buildResultsDisplayGroups(
  rows,
  sessionType,
  {
    splits = [],
    carClasses = [],
    combineSplits = false,
    combineClasses = false,
  } = {},
) {
  const hasSplitGrouping = !combineSplits && splits.length > 0
  const hasClassGrouping = !combineClasses && carClasses.length > 1
  const splitGroups = new Map()
  if (combineSplits || splits.length === 0) splitGroups.set(null, rows)
  else {
    for (const split of splits) splitGroups.set(split.id, [])
    for (const row of rows) {
      if (!splitGroups.has(row.splitId)) splitGroups.set(row.splitId, [])
      splitGroups.get(row.splitId).push(row)
    }
  }
  const splitById = new Map(splits.map((split) => [split.id, split]))
  const classById = new Map(
    carClasses.map((carClass) => [carClass.id, carClass]),
  )
  const groups = []
  for (const [splitId, splitRows] of splitGroups) {
    const classes = new Map()
    if (combineClasses || carClasses.length <= 1) classes.set(null, splitRows)
    else {
      for (const carClass of carClasses) classes.set(carClass.id, [])
      for (const row of splitRows) {
        const key = row.carClassId ?? 'unassigned'
        if (!classes.has(key)) classes.set(key, [])
        classes.get(key).push(row)
      }
    }
    for (const [carClassId, groupRows] of classes) {
      const split = splitById.get(splitId)
      const carClass =
        classById.get(carClassId) ?? groupRows[0]?.result.carClass
      const scope = hasClassGrouping
        ? 'Class'
        : hasSplitGrouping
          ? 'Split'
          : 'Overall'
      groups.push({
        id: JSON.stringify([splitId, carClassId]),
        splitId,
        carClassId,
        split,
        carClass,
        hasSplitGrouping,
        title:
          [
            hasSplitGrouping ? (split?.name ?? 'Unassigned split') : null,
            carClassId != null ? (carClass?.name ?? 'Unclassified') : null,
          ]
            .filter(Boolean)
            .join(' — ') || 'Overall results',
        scope,
        showSplits: combineSplits && splits.length > 1,
        showClasses: combineClasses && carClasses.length > 1,
        display: buildOverallDisplay(groupRows, sessionType, splits),
      })
    }
  }
  return groups
}
